import { addressDraft, addressTemplate, entries, scalar, type Answers } from "./lib/form-model";

export type StoredAddress = {
  mode?: string | null;
  country?: string | null;
  street_and_number?: string | null;
  building?: string | null;
  city?: string | null;
  department?: string | null;
  postal_code?: string | null;
  full_address?: string | null;
};

export type StoredOtherName = {
  given_name?: string | null;
  middle_name?: string | null;
  family_name?: string | null;
};

export type StoredPersonalDetails = {
  email?: string | null;
  mobile_number?: string | null;
  address?: StoredAddress | null;
  other_names_used?: string | null;
  other_names?: StoredOtherName[] | null;
};

/** Map the Personal details form into the English Mongo document. */
export function personalDetailsPayload(answers: Answers): StoredPersonalDetails {
  const draft = addressDraft(answers["pf.contact.address"]);
  const mode = draft.mode === "freeform" || !addressTemplate(draft.country) ? "freeform" : "structured";
  const names = entries(answers["pf.identity.other_names"]).map((name) => ({
    given_name: name.given_name || null,
    middle_name: name.middle_name || null,
    family_name: name.family_name || null,
  }));
  const usedRaw = scalar(answers["pf.identity.other_names_used"]).trim().toLowerCase();
  const other_names_used = usedRaw === "yes" || usedRaw === "no" ? usedRaw : names.length ? "yes" : undefined;
  return {
    email: scalar(answers["pf.contact.email"]).trim(),
    mobile_number: scalar(answers["pf.contact.phone"]).trim(),
    address: {
      mode,
      country: draft.country || "",
      street_and_number: draft.line1 || "",
      building: draft.line2 || "",
      city: draft.locality || "",
      department: draft.administrative_area || "",
      postal_code: draft.postal_code || "",
      full_address: draft.freeform || "",
    },
    other_names_used,
    other_names: other_names_used === "no" ? [] : names,
  };
}

/** Map a stored document back onto the form answers. */
export function answersFromPersonalDetails(data: StoredPersonalDetails | null | undefined): Answers {
  if (!data) return {};
  const next: Answers = {};
  const email = String(data.email || "").trim();
  const mobile = String(data.mobile_number || "").trim();
  if (email) next["pf.contact.email"] = email;
  if (mobile) next["pf.contact.phone"] = mobile;
  const address = data.address;
  if (address && typeof address === "object") {
    next["pf.contact.address"] = {
      mode: address.mode === "freeform" ? "freeform" : "structured",
      country: address.country || "",
      line1: address.street_and_number || "",
      line2: address.building || "",
      locality: address.city || "",
      administrative_area: address.department || "",
      postal_code: address.postal_code || "",
      freeform: address.full_address || "",
    };
  }
  const used = String(data.other_names_used || "").trim().toLowerCase();
  const names = (data.other_names || []).map((name) => ({
    given_name: name.given_name || "",
    middle_name: name.middle_name || "",
    family_name: name.family_name || "",
  })).filter((name) => name.given_name || name.middle_name || name.family_name);
  if (used === "yes" || used === "no") next["pf.identity.other_names_used"] = used;
  else if (names.length) next["pf.identity.other_names_used"] = "yes";
  if (names.length) next["pf.identity.other_names"] = names;
  return next;
}
